/**
 * SocialAssetUploader Component
 *
 * Drag-drop media uploader with reordering support.
 * Handles images and videos with platform compatibility checking.
 * Uses native HTML5 drag-drop to avoid additional dependencies.
 */
import { useState, useCallback, useRef } from 'react';
import {
  Upload,
  X,
  GripVertical,
  Image as ImageIcon,
  Video,
  AlertTriangle,
  Loader2,
  Maximize2,
} from 'lucide-react';

import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { uploads, social } from '@/services/api/admin';

const AssetItem = ({ asset, onRemove, onDragStart, onDragOver, onDrop, disabled, onPreview }) => {
  const hasIssues = Object.values(asset.compatibility_issues || {}).some(
    (issues) => issues?.length > 0
  );

  return (
    <TooltipProvider>
      <div
        draggable={!disabled}
        onDragStart={(e) => onDragStart(e, asset.id)}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, asset.id)}
        className={cn(
          'relative group rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700',
          'cursor-move select-none',
          hasIssues && 'border-amber-400'
        )}
      >
        {/* Drag handle */}
        {!disabled && (
          <div className="absolute top-2 left-2 z-10 p-1 rounded bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="h-4 w-4" />
          </div>
        )}

        {/* Preview button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPreview(asset);
          }}
          className="absolute top-2 right-10 z-10 p-1 rounded-full bg-gray-900/70 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-900"
        >
          <Maximize2 className="h-4 w-4" />
        </button>

        {/* Remove button */}
        {!disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(asset.id);
            }}
            className="absolute top-2 right-2 z-10 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/* Warning indicator */}
        {hasIssues && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="absolute bottom-2 left-2 z-10 p-1 rounded bg-amber-500 text-white">
                <AlertTriangle className="h-4 w-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <div className="space-y-1">
                <p className="font-medium">Compatibility Issues</p>
                {Object.entries(asset.compatibility_issues || {}).map(
                  ([platform, issues]) =>
                    issues?.length > 0 && (
                      <div key={platform}>
                        <p className="text-xs font-medium capitalize">{platform}:</p>
                        <ul className="text-xs list-disc pl-4">
                          {issues.map((issue, i) => (
                            <li key={i}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Media type indicator */}
        <div className="absolute bottom-2 right-2 z-10 p-1 rounded bg-black/50 text-white">
          {asset.asset_type === 'video' ? (
            <Video className="h-4 w-4" />
          ) : (
            <ImageIcon className="h-4 w-4" />
          )}
        </div>

        {/* Preview */}
        <div className="w-32 h-32">
          {asset.asset_type === 'video' ? (
            <video
              src={asset.media_url}
              className="w-full h-full object-cover pointer-events-none"
              muted
            />
          ) : (
            <img
              src={asset.thumbnail_url || asset.media_url}
              alt={asset.alt_text || ''}
              className="w-full h-full object-cover pointer-events-none"
              draggable={false}
            />
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

const SocialAssetUploader = ({
  assets = [],
  onChange,
  maxAssets = 10,
  disabled = false,
  className,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [previewAsset, setPreviewAsset] = useState(null);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const handleFiles = useCallback(
    async (files) => {
      if (disabled || files.length === 0) return;

      const remainingSlots = maxAssets - assets.length;
      const filesToUpload = Array.from(files).slice(0, remainingSlots);

      if (filesToUpload.length === 0) return;

      // Filter valid file types
      const validFiles = filesToUpload.filter(
        (file) =>
          file.type.startsWith('image/') || file.type.startsWith('video/')
      );

      if (validFiles.length === 0) return;

      setUploading(true);
      setUploadProgress(0);

      const newAssets = [];
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        try {
          // Determine asset type
          const assetType = file.type.startsWith('video/') ? 'video' : 'image';

          // Upload to S3
          const result = await uploads.uploadImage(file, {
            autoGallery: false,
            context: 'social',
          });

          // Create asset data object
          const assetData = {
            asset_type: assetType,
            media_url: result.url,
            thumbnail_url: result.thumbnail_url || result.url,
            original_filename: file.name,
            file_size_bytes: file.size,
            sort_order: assets.length + newAssets.length,
          };

          // Get image/video dimensions
          if (assetType === 'image') {
            const img = new Image();
            img.src = result.url;
            await new Promise((resolve) => {
              img.onload = () => {
                assetData.width = img.naturalWidth;
                assetData.height = img.naturalHeight;
                resolve();
              };
              img.onerror = resolve;
            });
          } else if (assetType === 'video') {
            const video = document.createElement('video');
            video.src = result.url;
            await new Promise((resolve) => {
              video.onloadedmetadata = () => {
                assetData.width = video.videoWidth;
                assetData.height = video.videoHeight;
                assetData.duration_seconds = video.duration;
                resolve();
              };
              video.onerror = resolve;
            });
          }

          // Save asset to backend database
          const savedAsset = await social.createAsset(assetData);
          newAssets.push(savedAsset);
          setUploadProgress(((i + 1) / validFiles.length) * 100);
        } catch (err) {
          console.error('Upload failed:', err);
          toast({
            title: 'Upload Failed',
            description: err.message || `Failed to upload ${file.name}`,
            variant: 'destructive',
          });
        }
      }

      setUploading(false);
      setUploadProgress(0);

      if (newAssets.length > 0) {
        onChange([...assets, ...newAssets]);
      }
    },
    [assets, maxAssets, onChange, disabled, toast]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (disabled || uploading) return;

    const files = e.dataTransfer?.files;
    if (files?.length > 0) {
      handleFiles(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled && !uploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files) {
      handleFiles(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Asset reordering via drag-drop
  const handleAssetDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleAssetDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleAssetDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      return;
    }

    const draggedIndex = assets.findIndex((a) => a.id === draggedId);
    const targetIndex = assets.findIndex((a) => a.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedId(null);
      return;
    }

    // Reorder
    const newAssets = [...assets];
    const [removed] = newAssets.splice(draggedIndex, 1);
    newAssets.splice(targetIndex, 0, removed);

    // Update sort_order
    const reordered = newAssets.map((a, i) => ({ ...a, sort_order: i }));
    onChange(reordered);
    setDraggedId(null);
  };

  const handleRemove = (assetId) => {
    onChange(assets.filter((a) => a.id !== assetId));
  };

  const handlePreview = (asset) => {
    setPreviewAsset(asset);
  };

  return (
    <div className={cn('space-y-4', className)}>
      <Label className="text-sm font-medium">
        Media ({assets.length}/{maxAssets})
      </Label>

      {/* Existing assets */}
      {assets.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {assets.map((asset) => (
            <AssetItem
              key={asset.id}
              asset={asset}
              onRemove={handleRemove}
              onDragStart={handleAssetDragStart}
              onDragOver={handleAssetDragOver}
              onDrop={handleAssetDrop}
              onPreview={handlePreview}
              disabled={disabled}
            />
          ))}
        </div>
      )}

      {/* Dropzone */}
      {assets.length < maxAssets && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
          className={cn(
            'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
            isDragOver
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500',
            (disabled || uploading) && 'opacity-50 cursor-not-allowed'
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileInput}
            className="hidden"
            disabled={disabled || uploading}
          />
          {uploading ? (
            <div className="space-y-2">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-gray-400 dark:text-gray-500 dark:text-gray-400" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Uploading... {Math.round(uploadProgress)}%
              </p>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isDragOver
                  ? 'Drop files here...'
                  : 'Drag & drop images or videos, or click to browse'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Supports JPG, PNG, GIF, MP4, MOV, WebM
              </p>
            </>
          )}
        </div>
      )}

      {/* Full-Size Preview Dialog */}
      <Dialog open={!!previewAsset} onOpenChange={(open) => !open && setPreviewAsset(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle className="text-lg font-semibold">
              {previewAsset?.asset_type === 'video' ? 'Video Preview' : 'Image Preview'}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            {previewAsset?.asset_type === 'video' ? (
              <video
                src={previewAsset?.media_url}
                controls
                autoPlay
                className="w-full max-h-[70vh] rounded-lg"
              />
            ) : (
              <img
                src={previewAsset?.media_url}
                alt={previewAsset?.alt_text || 'Full size preview'}
                className="w-full max-h-[70vh] object-contain rounded-lg"
              />
            )}
            {previewAsset?.original_filename && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                {previewAsset.original_filename}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialAssetUploader;
